import type { BoundingRect } from '@/layout/types';

/**
 * Compute tight bounding box from the actual SVG content
 * by reading the transform group's bounding box in layout coordinates.
 */
function computeSvgContentBounds(svgElement: SVGSVGElement): BoundingRect {
  const transformGroup = svgElement.querySelector('g') as SVGGElement | null;
  if (!transformGroup) {
    return { x: 0, y: 0, width: 800, height: 600 };
  }

  // Temporarily reset transform to get layout-coordinate bounds
  const savedTransform = transformGroup.getAttribute('transform') ?? '';
  transformGroup.setAttribute('transform', '');

  const bbox = transformGroup.getBBox();

  // Restore transform
  transformGroup.setAttribute('transform', savedTransform);

  return { x: bbox.x, y: bbox.y, width: bbox.width, height: bbox.height };
}

/**
 * Export the SVG canvas as a standalone SVG string.
 *
 * Clones the SVG element, strips the pan/zoom transform, removes interactive
 * UI elements, sets explicit dimensions via viewBox, and adds a background.
 */
export function exportSvg(svgElement: SVGSVGElement, _totalBounds?: BoundingRect): string {
  // Compute bounds from actual SVG DOM content (more accurate than layout re-computation)
  const contentBounds = computeSvgContentBounds(svgElement);
  const padding = 40;
  const width = contentBounds.width + padding * 2;
  const height = contentBounds.height + padding * 2;
  const viewBoxX = contentBounds.x - padding;
  const viewBoxY = contentBounds.y - padding;

  const clone = svgElement.cloneNode(true) as SVGSVGElement;

  // Remove the canvas-bg rect (full-screen transparent hit area)
  const canvasBg = clone.querySelector('.canvas-bg');
  if (canvasBg) canvasBg.remove();

  // Find the main transform group and reset its transform so nodes render
  // at their layout coordinates (the viewBox will frame them correctly).
  const transformGroup = clone.querySelector('g');
  if (transformGroup) {
    transformGroup.removeAttribute('transform');
  }

  // Remove interactive elements
  const selectorsToRemove = [
    'foreignObject',          // inline editor
    '.selection',             // selection overlay
  ];
  for (const selector of selectorsToRemove) {
    clone.querySelectorAll(selector).forEach((el) => el.remove());
  }

  // Remove collaborator name tags and drag ghosts
  clone.querySelectorAll('[data-drag-ghost]').forEach((el) => el.remove());

  // Set explicit dimensions and viewBox
  clone.setAttribute('width', String(Math.ceil(width)));
  clone.setAttribute('height', String(Math.ceil(height)));
  clone.setAttribute('viewBox', `${viewBoxX} ${viewBoxY} ${width} ${height}`);
  clone.removeAttribute('style');
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');

  // Insert a background rect as the very first child
  const bgRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
  bgRect.setAttribute('x', String(viewBoxX));
  bgRect.setAttribute('y', String(viewBoxY));
  bgRect.setAttribute('width', String(Math.ceil(width)));
  bgRect.setAttribute('height', String(Math.ceil(height)));
  bgRect.setAttribute('fill', '#1a1a2e');
  clone.insertBefore(bgRect, clone.firstChild);

  const serializer = new XMLSerializer();
  return serializer.serializeToString(clone);
}
